import { Landmark, MapPin, Store } from 'lucide-react';
import { Link, useParams } from 'react-router';
import { apiPaths, type PlaceResponse, type PlacesResponse } from '../../../shared/api/contract.ts';
import { DEFAULT_CITY } from '../../../shared/cities/index.ts';
import type { Place, PlaceCategory } from '../../../shared/domain/index.ts';
import { DemoBadge } from '../../components/FreshnessBadge.tsx';
import { PageContainer } from '../../components/layout/PageContainer.tsx';
import { PageHeader } from '../../components/PageHeader.tsx';
import { SectionCard } from '../../components/SectionCard.tsx';
import { useApi } from '../../hooks/useApi.ts';
import { formatDay } from '../../lib/format.ts';
import { PLACE_CATEGORY_LABELS } from './labels.ts';
import { formatOsmOpeningHours } from './opening-hours.ts';

/** Commerces et équipements : trop nombreux pour une liste plate, groupés par catégorie. */
function ShopsByCategory({ shops }: { shops: readonly Place[] }) {
  const groups = new Map<PlaceCategory, Place[]>();
  for (const shop of shops) {
    const list = groups.get(shop.category) ?? [];
    list.push(shop);
    groups.set(shop.category, list);
  }
  const sorted = [...groups.entries()].toSorted((a, b) => b[1].length - a[1].length);

  return (
    <>
      {sorted.map(([category, list]) => (
        <details key={category} className="place-group">
          <summary>
            {PLACE_CATEGORY_LABELS[category]} <span className="place-group__count">({list.length})</span>
          </summary>
          <ul className="item-list">
            {list.map((p) => (
              <li key={p.id}>
                <Link to={`/lieux/${p.slug}`}>{p.name}</Link>
                {p.address !== undefined && <span className="item-list__meta">{p.address}</span>}
              </li>
            ))}
          </ul>
        </details>
      ))}
    </>
  );
}

export function PlacesPage() {
  const places = useApi<PlacesResponse>(apiPaths.places(DEFAULT_CITY.slug));
  const shops = useApi<PlacesResponse>(apiPaths.shops(DEFAULT_CITY.slug));
  return (
    <PageContainer path="/lieux">
      <SectionCard
        title="Services publics"
        icon={<Landmark size={20} aria-hidden="true" />}
        kind="places"
        section={places.data?.places}
        loading={places.loading}
        fetchError={places.error}
      >
        {(list) => (
          <ul className="item-list">
            {list.map((p) => (
              <li key={p.id}>
                <Link to={`/lieux/${p.slug}`}>
                  <strong>{p.name}</strong>
                </Link>
                <span className="item-list__meta">
                  {PLACE_CATEGORY_LABELS[p.category]}
                  {p.address !== undefined && ` · ${p.address}`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
      <SectionCard
        title="Commerces et lieux utiles"
        icon={<Store size={20} aria-hidden="true" />}
        kind="places"
        section={shops.data?.places}
        loading={shops.loading}
        fetchError={shops.error}
      >
        {(list) => <ShopsByCategory shops={list} />}
      </SectionCard>
    </PageContainer>
  );
}

export function PlacePage() {
  const { slug = '' } = useParams();
  const place = useApi<PlaceResponse>(apiPaths.place(DEFAULT_CITY.slug, slug));
  const p = place.data?.place;
  return (
    <div className="container page">
      {p === undefined && <PageHeader path={`/lieux/${slug}`} />}
      {p !== undefined && (
        <article className="card place-detail" aria-labelledby="place-title">
          <title>{`${p.name}, ${DEFAULT_CITY.name} (${DEFAULT_CITY.department.code}) | Ermont Live`}</title>
          <meta
            name="description"
            content={`${p.name} : ${PLACE_CATEGORY_LABELS[p.category].toLowerCase()} à ${DEFAULT_CITY.name} (${DEFAULT_CITY.department.name}, ${DEFAULT_CITY.department.code}), ${DEFAULT_CITY.region}.`}
          />
          {p.source.provider === 'fixture' && <DemoBadge />}
          <h1 className="page-title" id="place-title">
            <MapPin size={22} aria-hidden="true" /> {p.name}
          </h1>
          <p className="page-intro">
            {PLACE_CATEGORY_LABELS[p.category]} à {DEFAULT_CITY.name} ({DEFAULT_CITY.department.name},{' '}
            {DEFAULT_CITY.department.code})
          </p>
          <dl>
            {p.address !== undefined && (
              <>
                <dt>Adresse</dt>
                <dd>{p.address}</dd>
              </>
            )}
            {p.openingHours !== undefined && (
              <>
                <dt>Horaires</dt>
                <dd>{p.source.provider === 'osm' ? formatOsmOpeningHours(p.openingHours) : p.openingHours}</dd>
              </>
            )}
            {p.phone !== undefined && (
              <>
                <dt>Téléphone</dt>
                <dd>
                  <a href={`tel:${p.phone}`}>{p.phone}</a>
                </dd>
              </>
            )}
            {p.website !== undefined && (
              <>
                <dt>Site</dt>
                <dd>
                  <a href={p.website} rel="noopener noreferrer" target="_blank">
                    {p.website}
                  </a>
                </dd>
              </>
            )}
          </dl>
          {p.source.sourceUrl !== undefined && (
            <p className="place-source">
              Source :{' '}
              <a href={p.source.sourceUrl} rel="noopener noreferrer" target="_blank">
                {p.source.provider === 'osm' ? 'OpenStreetMap' : 'fiche officielle'}
              </a>
              {p.source.observedAt !== undefined && <> · mise à jour le {formatDay(p.source.observedAt)}</>}
            </p>
          )}
        </article>
      )}
      {place.error !== null && p === undefined && <p className="state-message">Lieu introuvable ou indisponible.</p>}
      <Link className="text-link" to="/lieux">
        ← Tous les lieux
      </Link>
    </div>
  );
}
